import React from 'react';
import { Tooltip as PloneTooltip } from '@plone/components';
import { clsx } from 'clsx';
import './Tooltip.scss';

export interface ESSTooltipProps {
  placement?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  children?: React.ReactNode;
}

/**
 * ESS Tooltip Component
 *
 * A tooltip component that displays contextual information on hover or focus.
 * Extends @plone/components Tooltip with ESS design system styling.
 *
 * Note: In React Aria Components, Tooltip is typically used with a Button or other
 * trigger element. The trigger element should have aria-describedby pointing to the tooltip.
 *
 * @example
 * ```tsx
 * import { ESSTooltip } from './components/Atoms/Tooltip';
 * import { Button } from './components/Atoms/Button';
 *
 * <Button aria-describedby="tooltip-1">
 *   Hover me
 * </Button>
 * <ESSTooltip id="tooltip-1" placement="top">
 *   This is helpful information
 * </ESSTooltip>
 * ```
 */
export const ESSTooltip: React.FC<ESSTooltipProps> = ({
  className,
  placement = 'top',
  children,
  ...props
}) => {
  return (
    <div
      className={clsx('ess-tooltip', `ess-tooltip--${placement}`, className)}
    >
      <PloneTooltip {...props}>{children}</PloneTooltip>
    </div>
  );
};

export default ESSTooltip;
